import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useEpicRequestsPaged } from "@/hooks/queries/use-epic-requests";
import { useEpicsPaged } from "@/hooks/queries/use-epics";
import { useTeam, useTeamChildren, useTeamDescendants } from "@/hooks/queries/use-teams";
import { TeamCard } from "@/components/teams/team-card";
import { EntityCard } from "@/components/ui/entity-card";
import { ExternalLink, Lightbulb } from "lucide-react";
import type { EpicRequestWithReactionCounts } from "@/lib/api/epic-requests";

interface TeamEpicsTabProps {
  teamId: string;
}

function TeamStatCard({
  label,
  value,
  description,
}: {
  label: string;
  value: ReactNode;
  description: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <div className="mt-2 text-3xl font-semibold text-foreground">{value}</div>
      <div className="mt-1 text-sm text-muted-foreground">{description}</div>
    </div>
  );
}

function EpicRequestCard({ request }: { request: EpicRequestWithReactionCounts }) {
  const hueColor =
    request.status === "approved"
      ? "#22C55E"
      : request.status === "pending"
        ? "#EAB308"
        : request.status === "planning"
          ? "#8B5CF6"
          : request.status === "converted"
            ? "#3B82F6"
            : "#EF4444";

  return (
    <EntityCard
      href={`/epic-requests/${request.id}`}
      hueColor={hueColor}
      icon={<Lightbulb className="h-5 w-5" style={{ color: hueColor }} />}
      title={request.identifier ? `${request.identifier} — ${request.title}` : request.title}
      subtitle={request.description}
      meta={request.status}
      secondaryMeta={request.team?.key ?? undefined}
      footer={
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{request.requestedBy?.name ?? "Unknown requester"}</span>
          <span className="inline-flex items-center gap-1 text-foreground">
            Open
            <ExternalLink className="h-3 w-3" />
          </span>
        </div>
      }
    />
  );
}

export function TeamEpicsTab({ teamId }: TeamEpicsTabProps) {
  const { data: team, isLoading } = useTeam(teamId);
  const { data: childTeams } = useTeamChildren(teamId);
  const { data: descendants } = useTeamDescendants(teamId);
  const { data: epicsData } = useEpicsPaged({ teamId, limit: 1 });
  const { data: requestsData } = useEpicRequestsPaged({ limit: 100, status: "approved" });

  const teamRequests = useMemo(
    () => (requestsData?.data ?? []).filter((request) => request.teamId === teamId),
    [requestsData?.data, teamId],
  );

  if (isLoading || !team) {
    return (
      <div className="rounded-2xl border border-dashed border-white/[0.08] px-4 py-8 text-center text-sm text-muted-foreground">
        Loading epics and requests…
      </div>
    );
  }

  const directChildren = childTeams ?? [];
  const allDescendants = descendants ?? [];
  const leafDescendantCount = allDescendants.filter((descendant) => descendant.canOwnEpics).length;
  const epicCount = epicsData?.meta?.totalCount ?? team._count?.epics ?? 0;
  const memberCount = team._count?.memberships ?? 0;

  if (team.canOwnEpics) {
    return (
      <div className="space-y-6">
        <div className="grid gap-3 md:grid-cols-3">
          <TeamStatCard
            label="Epics"
            value={epicCount}
            description={
              <Link
                className="inline-flex items-center gap-1 text-foreground hover:text-primary"
                to={`/epics?teamId=${team.id}`}
              >
                Browse on epics page
                <ExternalLink className="h-4 w-4" />
              </Link>
            }
          />
          <TeamStatCard
            label="Approved requests"
            value={teamRequests.length}
            description="Approved requests waiting or already planning"
          />
          <TeamStatCard
            label="Members"
            value={memberCount}
            description="People directly assigned to this team"
          />
        </div>

        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">Epic requests</h2>
            <p className="text-sm text-muted-foreground">
              Approved requests already associated with this discipline.
            </p>
          </div>
          {teamRequests.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/[0.08] px-4 py-8 text-center text-sm text-muted-foreground">
              No approved Epic Requests linked to this discipline yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {teamRequests.slice(0, 6).map((request) => (
                <EpicRequestCard key={request.id} request={request} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-3">
        <TeamStatCard
          label="Direct children"
          value={directChildren.length}
          description="Immediate teams nested beneath this one"
        />
        <TeamStatCard
          label="Descendants"
          value={allDescendants.length}
          description="Total teams below this point in the tree"
        />
        <TeamStatCard
          label="Epic owner teams"
          value={leafDescendantCount}
          description="Leaf disciplines available lower in the hierarchy"
        />
      </div>

      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Child teams</h2>
          <p className="text-sm text-muted-foreground">
            Epics and requests are managed from child teams beneath this parent.
          </p>
        </div>
        {directChildren.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/[0.08] px-4 py-8 text-center text-sm text-muted-foreground">
            No child teams are linked here yet. Epics and requests are managed from child teams.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {directChildren.map((childTeam) => (
              <TeamCard key={childTeam.id} team={childTeam} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
